import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const ProfileSettings = () => {
  const { user, setUser } = useAuth(); // we need setUser if we want to update context after name change
  const [name, setName] = useState(user?.name || '');
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState({ profile: false, password: false });

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setLoading((prev) => ({ ...prev, profile: true }));
    try {
      const response = await authAPI.updateProfile({ name });
      // Update user in context
      setUser((prev) => ({ ...prev, name: response.data.name }));
      toast.success('Name updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update name');
    } finally {
      setLoading((prev) => ({ ...prev, profile: false }));
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading((prev) => ({ ...prev, password: true }));
    try {
      await authAPI.updatePassword({
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword,
      });
      toast.success('Password updated successfully');
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setLoading((prev) => ({ ...prev, password: false }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-xl p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Profile Settings</h1>
            <p className="opacity-90 mt-1">Manage your account information</p>
          </div>
          <div className="hidden md:block">
            <i className="fas fa-user-cog text-5xl opacity-20"></i>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Update Name Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <i className="fas fa-user mr-2 text-primary-600"></i>
            Edit Name
          </h2>
          <form onSubmit={handleNameSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Your full name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email (cannot be changed)
              </label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
              />
            </div>
            <button
              type="submit"
              disabled={loading.profile}
              className="w-full bg-primary-600 text-gray-700 py-2 px-4 rounded-lg border border-gray-300 shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
            >
              {loading.profile ? (
                <><i className="fas fa-spinner fa-spin mr-2"></i> Updating...</>
              ) : (
                'Update Name'
              )}
            </button>
          </form>
        </div>

        {/* Change Password Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <i className="fas fa-lock mr-2 text-primary-600"></i>
            Change Password
          </h2>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="At least 6 characters"
                required
                minLength={6}
                autoComplete="new-password"  // Prevents browser from suggesting old passwords
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Re-enter new password"
                required
                minLength={6}
                autoComplete="new-password"  // Consistent with new password field
              />
            </div>
            <button
              type="submit"
              disabled={loading.password}
              className="w-full bg-primary-600 text-gray-700 py-2 px-4 rounded-lg border border-gray-300 shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
            >
              {loading.password ? (
                <><i className="fas fa-spinner fa-spin mr-2"></i> Updating...</>
              ) : (
                'Update Password'
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Optional: Info box */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md text-sm text-blue-800">
        <div className="flex">
          <div className="flex-shrink-0">
            <i className="fas fa-info-circle text-blue-500 mr-2"></i>
          </div>
          <div>
            <p>
              Your email address cannot be changed because it is used for login.
              If you need to change your email, please contact your system administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;